import { LogsEvent, datadogLogs } from '@datadog/browser-logs'
import { DefaultPrivacyLevel, datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'

// browser SDK は Datadog Agent を経由しないため、ここで渡す env がそのまま
// indexed log の env タグになる。社内標準の短縮形に正規化して
// env:production / env:staging の混在を防ぐ。Vite の MODE を使う
// サンプリング等の判定は正規化前の値のまま維持する。
const normalizeDatadogEnv = (mode: string): string => {
  switch (mode) {
    case 'production':
      return 'prod'
    case 'staging':
      return 'stg'
    case 'develop':
    case 'development':
      return 'dev'
    default:
      return mode
  }
}

// fetch がレスポンスを受け取れなかった通信失敗（status 0）はクライアント側の
// 回線断などでサーバーに届いていないため、error ではなく warn として送る
const downgradeClientNetworkError = (event: LogsEvent): void => {
  if (event.origin === 'network' && event.http?.status_code === 0) {
    event.status = 'warn'
  }
}

export const initializeDatadogRUM = (): void => {
  const applicationId = import.meta.env.VITE_DD_APPLICATION_ID
  const clientToken = import.meta.env.VITE_DD_CLIENT_TOKEN
  const env = import.meta.env.MODE
  const datadogEnv = normalizeDatadogEnv(env)
  const service = import.meta.env.VITE_DD_SERVICE_NAME
  const apiBaseUrl = import.meta.env.VITE_API_URL as string | undefined

  if (!clientToken || !applicationId) {
    console.warn(
      'Datadog RUM: Missing required configuration (clientToken or applicationId)',
    )
    return
  }

  let defaultPrivacyLevel: DefaultPrivacyLevel = DefaultPrivacyLevel.MASK

  if (env === 'development') {
    defaultPrivacyLevel = DefaultPrivacyLevel.MASK_USER_INPUT
  }

  const allowedTrackingOrigins: Array<string | RegExp> = []
  if (apiBaseUrl) {
    try {
      const url = new URL(apiBaseUrl)
      allowedTrackingOrigins.push(url.origin)
    } catch (e) {
      console.warn(
        'Datadog RUM: invalid VITE_API_URL, skip tracing origins',
        apiBaseUrl,
        e,
      )
    }
  }

  datadogRum.init({
    applicationId,
    clientToken,
    service,
    env: datadogEnv,
    version: import.meta.env.VITE_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    traceSampleRate: 100,
    traceContextInjection: 'all',
    defaultPrivacyLevel,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    enablePrivacyForActionName: true,
    allowedTracingUrls: allowedTrackingOrigins,
    plugins: [reactPlugin()],
  })

  datadogRum.startSessionReplayRecording({ force: true })
  datadogRum.startView({
    service,
  })

  datadogLogs.init({
    clientToken,
    version: import.meta.env.VITE_APP_VERSION,
    service,
    env: datadogEnv,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    sessionSampleRate: 100,
    beforeSend: (event) => {
      downgradeClientNetworkError(event)
      return true
    },
  })

  console.log('Datadog RUM initialized successfully')
}
